import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useAppDispatch, useTrading } from "features/store";
import {
  useListAccountStrategiesQuery,
  useGetRemovedStrategiesQuery,
  Strategy,
} from "features/trading/accounts";
import {
  SelectedStrategyKind,
  setSelectedStrategy,
} from "features/trading/tradingSlice";
import { useEffect, useRef } from "react";

const optionValue = (kind: SelectedStrategyKind, id: number) => `${kind}-${id}`;

interface AccountStrategyPickerProps {
  initialStrategyId?: number | null;
}

export const AccountStrategyPicker = ({ initialStrategyId }: AccountStrategyPickerProps) => {
  const dispatch = useAppDispatch();
  const { currentAccountId, selectedStrategy } = useTrading();
  const initialSelectionApplied = useRef(false);

  useEffect(() => {
    dispatch(setSelectedStrategy(undefined));
    initialSelectionApplied.current = false;
  }, [currentAccountId, dispatch]);

  const { data, isLoading } = useListAccountStrategiesQuery(
    { filters: { account_id: currentAccountId } },
    { skip: !currentAccountId },
  );

  const { data: removedData, isLoading: isRemovedLoading } =
    useGetRemovedStrategiesQuery(currentAccountId, {
      skip: !currentAccountId,
    });

  const strategies = data?.results ?? [];
  const removedStrategies: Strategy[] = removedData?.results ?? [];
  const hasRemoved = removedStrategies.length > 0;

  useEffect(() => {
    if (initialSelectionApplied.current || !initialStrategyId || !strategies.length) return;
    const matching = strategies.find((s) => s.strategy === initialStrategyId);
    if (matching) {
      dispatch(setSelectedStrategy({ kind: SelectedStrategyKind.ACTIVE, data: matching }));
      initialSelectionApplied.current = true;
    }
  }, [initialStrategyId, strategies, dispatch]);

  if (isLoading || !data || isRemovedLoading) {
    return <CircularProgress />;
  }

  const selectValue = selectedStrategy
    ? optionValue(selectedStrategy.kind, selectedStrategy.data.id)
    : "";

  return (
    <Box display="flex" alignItems="center">
      <Select
        labelId="account-strategy-label"
        label="Strategy"
        size="small"
        value={selectValue}
        sx={{ width: 250, borderRadius: 5 }}
        onChange={(event) => {
          const raw = event.target.value as string;
          const [kind, idRaw] = raw.split("-");
          const id = Number(idRaw);

          if (kind === SelectedStrategyKind.ACTIVE) {
            const active = strategies.find((s) => s.id === id);
            if (active) {
              dispatch(
                setSelectedStrategy({
                  kind: SelectedStrategyKind.ACTIVE,
                  data: active,
                }),
              );
            }
            return;
          }

          if (kind === SelectedStrategyKind.REMOVED) {
            const removed = removedStrategies.find((s) => s.id === id);
            if (removed && currentAccountId !== undefined) {
              dispatch(
                setSelectedStrategy({
                  kind: SelectedStrategyKind.REMOVED,
                  data: removed,
                  accountId: currentAccountId,
                }),
              );
            }
          }
        }}
        disabled={strategies.length === 0 && !hasRemoved}
      >
        {strategies.map((strat) => (
          <MenuItem
            key={`${SelectedStrategyKind.ACTIVE}-${strat.id}`}
            value={optionValue(SelectedStrategyKind.ACTIVE, strat.id)}
          >
            {strat.strategy__name}
          </MenuItem>
        ))}
        {hasRemoved &&
          removedStrategies.map((strat) => (
            <MenuItem
              key={`${SelectedStrategyKind.REMOVED}-${strat.id}`}
              value={optionValue(SelectedStrategyKind.REMOVED, strat.id)}
            >
              {strat.name}{" "}
              <Typography component="span" variant="caption" color="grey.500">
                (Removed)
              </Typography>
            </MenuItem>
          ))}
      </Select>
    </Box>
  );
};
